import { deleteHttp } from "../../../common/api/apiShared.ts";
import { createElement, useState } from "react";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { useMasterDataBreadCrumbProvider } from "../../useMasterDataBreadCrumbProvider.ts";
import { Trans } from "react-i18next";
import { ContractTypeViewModel } from "./ContractType.types.ts";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export default function useContractTypesHooks() {
    const url = "/contractTypes";
    const { isLoading, data: contractTypes, refetch } = useDotLegalQuery<Array<ContractTypeViewModel>>(url);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const { translateString } = useTranslation();
    const [contractTypeForDeletion, setContractTypeForDeletion] = useState<ContractTypeViewModel | undefined>(undefined);
    const [contractTypeForUpdate, setContractTypeForUpdate] = useState<ContractTypeViewModel | undefined>();
    const snackBar = useDotLegalSnackbar();

    const { permissions } = useUserContext();
    const masterDataBreadCrumbProvider = useMasterDataBreadCrumbProvider();
    const breadCrumbs = masterDataBreadCrumbProvider.getBreadCrumbs(translateString("contractTypes"));

    const deleteContractType = async (id: string) => {
        return await deleteHttp(`/contractTypes/${id}`);
    };

    const deleteMutation = useDotLegalMutation(deleteContractType, {
        onSuccess: async (result) => {
            if (result.success()) {
                await refetch();
                snackBar.show(createElement(Trans, { i18nKey: "itemDeletedSuccess", values: { name: contractTypeForDeletion?.name } }));
            } else {
                snackBar.show(createElement(Trans, { i18nKey: "cantDeleteContractType", values: { name: contractTypeForDeletion?.name } }), "warning");
            }
            setContractTypeForDeletion(undefined);
        },
    });

    const onCreateClick = () => {
        setShowCreateDialog(true);
    };

    const onSaved = async () => {
        await refetch();
        onDialogClose();
    };

    const onDialogClose = () => {
        setContractTypeForUpdate(undefined);
        setShowCreateDialog(false);
    };

    const onDelete = () => {
        deleteMutation.mutate(contractTypeForDeletion!.id);
    };

    const getMenuItems = (contractType: ContractTypeViewModel) => {
        let menuItems = [];
        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setContractTypeForUpdate(contractType);
                },
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setContractTypeForDeletion(contractType);
                },
            });
        }

        return menuItems;
    };

    return {
        isLoading,
        contractTypes,
        breadCrumbs,
        getMenuItems,
        canManageMasterData: permissions.canManageMasterData,
        showCreateDialog,
        onSaved,
        contractTypeForDeletion,
        contractTypeForUpdate,
        setContractTypeForDeletion,
        onDelete,
        onCreateClick,
        onDialogClose,
        isDeleting: deleteMutation.isLoading,
    };
}
